import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '../lib/errors';
import AlertasLead from './AlertasLead';

interface Conversa {
  id: number;
  contato: string | null;
  telefone: string | null;
  tipo: string | null;
  mensagem: string | null;
  direcao: string | null;
  data: string | null;
  arquivo_origem: string | null;
  importado_em: string | null;
  palavras_chave: string[] | null;
}

// Cores dos badges por palavra-chave (evitando azul/roxo)
const PALAVRA_CORES: Record<string, string> = {
  'Obra': 'bg-amber-100 text-amber-800 border-amber-200',
  'Orçamento': 'bg-green-100 text-green-800 border-green-200',
  'Mesa': 'bg-orange-100 text-orange-800 border-orange-200',
  'Cadeira': 'bg-rose-100 text-rose-800 border-rose-200',
  'Escritório': 'bg-teal-100 text-teal-800 border-teal-200',
  'Entrega': 'bg-lime-100 text-lime-800 border-lime-200',
};

const PALAVRAS_ORDEM = ['Obra', 'Orçamento', 'Mesa', 'Cadeira', 'Escritório', 'Entrega'];

interface WhatsAppProps {
  password: string;
}

function formatDataHora(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function WhatsAppConversas({ password }: WhatsAppProps) {
  const [conversas, setConversas] = useState<Conversa[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [filtroContato, setFiltroContato] = useState('');
  const [filtroPalavra, setFiltroPalavra] = useState('');
  const [offset, setOffset] = useState(0);
  const LIMIT = 50;

  const [resumo, setResumo] = useState<{ resumo: Record<string, number>; totalComTags: number; totalConversas: number } | null>(null);
  const [reprocessando, setReprocessando] = useState(false);
  const [reprocessMsg, setReprocessMsg] = useState<string | null>(null);

  const [importFile, setImportFile] = useState<File | null>(null);
  const [importData, setImportData] = useState<any[]>([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const loadConversas = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase.functions.invoke('crm-whatsapp', {
        body: {
          action: 'listar',
          password,
          search: search || undefined,
          contato: filtroContato || undefined,
          palavra: filtroPalavra || undefined,
          limit: LIMIT,
          offset,
        },
      });
      if (error) throw new Error(await extractErrorMessage(error));
      const parsed = data as { conversas: Conversa[]; count: number; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setConversas(parsed.conversas ?? []);
      setTotalCount(parsed.count ?? 0);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar conversas.');
    } finally {
      setLoading(false);
    }
  }, [password, search, filtroContato, filtroPalavra, offset]);

  const loadResumo = useCallback(async () => {
    try {
      const { data, error } = await supabase.functions.invoke('crm-whatsapp', {
        body: { action: 'resumo', password },
      });
      if (error) return;
      const parsed = data as { resumo: Record<string, number>; totalComTags: number; totalConversas: number; error?: string };
      if (parsed?.error) return;
      setResumo(parsed);
    } catch {
      // silencioso
    }
  }, [password]);

  useEffect(() => {
    loadConversas();
  }, [loadConversas]);

  useEffect(() => {
    loadResumo();
  }, [loadResumo]);

  const handleFile = async (file: File) => {
    setImportFile(file);
    setImportError(null);
    setImportResult(null);
    setImportData([]);
    try {
      const nome = file.name.toLowerCase();
      let rows: any[] = [];

      if (nome.endsWith('.json')) {
        const text = await file.text();
        const json = JSON.parse(text);
        rows = Array.isArray(json) ? json : (json.messages ?? json.conversas ?? json.chats ?? []);
      } else if (nome.endsWith('.csv') || nome.endsWith('.txt')) {
        const text = await file.text();
        rows = parseCSV(text);
      } else {
        setImportError('Formato não suportado. Envie CSV, TXT ou JSON.');
        return;
      }

      const mapped = mapRowsToConversas(rows, nome);
      setImportData(mapped);
    } catch (e) {
      setImportError(e instanceof Error ? e.message : 'Erro ao ler arquivo.');
    }
  };

  const doImport = async () => {
    if (!importData.length) return;
    setImportLoading(true);
    setImportError(null);
    setImportResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('crm-whatsapp', {
        body: { action: 'importar', password, conversas: importData },
      });
      if (error) throw new Error(await extractErrorMessage(error));
      const parsed = data as { importadas?: number; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setImportResult({ ok: true, msg: `${parsed.importadas ?? 0} conversas importadas com sucesso!` });
      setImportFile(null);
      setImportData([]);
      await loadConversas();
    } catch (e) {
      setImportError(e instanceof Error ? e.message : 'Erro ao importar.');
    } finally {
      setImportLoading(false);
    }
  };

  const reprocessar = async () => {
    setReprocessando(true);
    setReprocessMsg(null);
    try {
      const { data, error } = await supabase.functions.invoke('crm-whatsapp', {
        body: { action: 'reprocessar', password, todas: true },
      });
      if (error) throw new Error(await extractErrorMessage(error));
      const parsed = data as { atualizadas?: number; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setReprocessMsg(`${parsed.atualizadas ?? 0} mensagens reprocessadas.`);
      await loadResumo();
      await loadConversas();
    } catch (e) {
      setReprocessMsg(e instanceof Error ? e.message : 'Erro ao reprocessar.');
    } finally {
      setReprocessando(false);
    }
  };

  const totalPages = Math.ceil(totalCount / LIMIT);

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-green-50 text-green-600 rounded-xl">
            <i className="ri-whatsapp-line text-lg"></i>
          </div>
          <div>
            <h2 className="font-bold text-gray-900">Conversas do WhatsApp</h2>
            <p className="text-sm text-gray-500">
              Importe conversas exportadas do WhatsApp Web e busque por contato ou conteúdo
            </p>
          </div>
        </div>
        <span className="inline-flex items-center gap-1.5 text-xs font-bold bg-green-50 text-green-700 border border-green-200 rounded-full px-3 py-1">
          <i className="ri-message-3-line"></i>
          {totalCount} mensagem(ns)
        </span>
      </div>

      {/* Alertas de lead — contatos que falaram de obra/orçamento */}
      <AlertasLead password={password} />

      {/* Resumo de palavras-chave */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-amber-50 text-amber-600 rounded-xl">
              <i className="ri-focus-2-line text-lg"></i>
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Detecção automática de palavras-chave</h3>
              <p className="text-sm text-gray-500">
                {resumo?.totalComTags ?? 0} de {resumo?.totalConversas ?? 0} mensagens têm tema detectado (obra, orçamento, mesa, cadeira...)
              </p>
            </div>
          </div>
          <button
            onClick={reprocessar}
            disabled={reprocessando}
            className="inline-flex items-center gap-2 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-lg cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60"
          >
            <i className={`${reprocessando ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'}`}></i>
            {reprocessando ? 'Reprocessando...' : 'Reprocessar tudo'}
          </button>
        </div>

        {reprocessMsg && (
          <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3 mb-4">{reprocessMsg}</div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {PALAVRAS_ORDEM.map((palavra) => {
            const total = resumo?.resumo?.[palavra] ?? 0;
            return (
              <button
                key={palavra}
                onClick={() => { setFiltroPalavra(filtroPalavra === palavra ? '' : palavra); setOffset(0); }}
                className={`flex flex-col items-start gap-1 rounded-xl border p-3 text-left transition-colors cursor-pointer ${
                  filtroPalavra === palavra
                    ? 'border-gray-900 bg-gray-900 text-white'
                    : 'border-gray-200 bg-gray-50/60 hover:bg-gray-100'
                }`}
              >
                <span className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 border ${
                  filtroPalavra === palavra ? 'bg-white/20 border-white/30 text-white' : (PALAVRA_CORES[palavra] ?? 'bg-gray-100 text-gray-700 border-gray-200')
                }`}>
                  {palavra}
                </span>
                <span className={`text-2xl font-bold ${filtroPalavra === palavra ? 'text-white' : 'text-gray-900'}`}>{total}</span>
                <span className={`text-[11px] ${filtroPalavra === palavra ? 'text-gray-300' : 'text-gray-500'}`}>mensagens</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Importação */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 flex items-center justify-center bg-green-50 text-green-600 rounded-xl">
            <i className="ri-file-transfer-line text-lg"></i>
          </div>
          <div>
            <h3 className="font-bold text-gray-900">Importar conversas exportadas</h3>
            <p className="text-sm text-gray-500">
              Use uma extensão do Chrome (ex: WhatsApp Scraper, WA Chats Backup) pra exportar e suba o arquivo aqui.
            </p>
          </div>
        </div>

        <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center">
          <input
            id="whatsapp-file"
            type="file"
            accept=".csv,.txt,.json"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
            }}
            className="hidden"
          />
          <label htmlFor="whatsapp-file" className="cursor-pointer inline-flex flex-col items-center gap-2">
            <div className="w-12 h-12 flex items-center justify-center bg-gray-100 text-gray-500 rounded-full">
              <i className="ri-upload-cloud-2-line text-2xl"></i>
            </div>
            <span className="text-sm font-semibold text-gray-700">
              {importFile?.name ?? 'Clique para escolher o arquivo'}
            </span>
            <span className="text-xs text-gray-400">CSV, TXT ou JSON</span>
          </label>
        </div>

        {importData.length > 0 && (
          <div className="mt-4">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-sm text-gray-700">
                <strong>{importData.length}</strong> mensagens lidas
              </span>
              <button
                onClick={doImport}
                disabled={importLoading}
                className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 px-4 py-2 rounded-xl cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60"
              >
                <i className={`${importLoading ? 'ri-loader-4-line animate-spin' : 'ri-check-line'}`}></i>
                {importLoading ? 'Importando...' : 'Confirmar importação'}
              </button>
              <button
                onClick={() => { setImportData([]); setImportFile(null); }}
                className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-xl cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-close-line"></i>
                Cancelar
              </button>
            </div>
            <div className="max-h-52 overflow-y-auto border border-gray-200 rounded-xl">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left">Contato</th>
                    <th className="px-3 py-2 text-left">Telefone</th>
                    <th className="px-3 py-2 text-left">Mensagem</th>
                    <th className="px-3 py-2 text-left">Data</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {importData.slice(0, 15).map((r, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2 font-medium">{r.contato}</td>
                      <td className="px-3 py-2">{r.telefone}</td>
                      <td className="px-3 py-2 max-w-xs truncate">{r.mensagem}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{formatDataHora(r.data)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {importData.length > 15 && (
                <p className="text-xs text-gray-400 px-3 py-2">...e mais {importData.length - 15} mensagens</p>
              )}
            </div>
          </div>
        )}

        {importError && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mt-4">{importError}</div>
        )}
        {importResult && (
          <div className={`text-sm border rounded-lg px-4 py-3 mt-4 ${importResult.ok ? 'text-green-700 bg-green-50 border-green-200' : 'text-red-600 bg-red-50 border-red-200'}`}>
            {importResult.msg}
          </div>
        )}
      </div>

      {/* Busca e filtros */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Buscar</label>
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
                placeholder="Buscar por contato, telefone ou conteúdo da mensagem..."
                className="w-full border border-gray-200 rounded-xl pl-9 pr-4 py-2 text-sm text-gray-800 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 bg-white"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Contato</label>
            <input
              type="text"
              value={filtroContato}
              onChange={(e) => { setFiltroContato(e.target.value); setOffset(0); }}
              placeholder="Filtrar por nome do contato..."
              className="w-full border border-gray-200 rounded-xl px-4 py-2 text-sm text-gray-800 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 bg-white"
            />
          </div>
        </div>
      </div>

      {/* Aviso de filtro ativo */}
      {filtroPalavra && (
        <div className="flex items-center justify-between gap-3 bg-gray-900 text-white rounded-xl px-4 py-3">
          <span className="text-sm">
            Filtrando por palavra-chave: <strong>{filtroPalavra}</strong> — {totalCount} mensagem(ns)
          </span>
          <button
            onClick={() => { setFiltroPalavra(''); setOffset(0); }}
            className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 cursor-pointer whitespace-nowrap transition-colors"
          >
            <i className="ri-close-line"></i> Limpar filtro
          </button>
        </div>
      )}

      {/* Lista de conversas */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3 m-4">{error}</div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50/80 text-gray-500 text-xs font-semibold uppercase tracking-wider">
                <th className="px-4 py-3 text-left">Contato</th>
                <th className="px-4 py-3 text-left">Telefone</th>
                <th className="px-4 py-3 text-left">Mensagem</th>
                <th className="px-4 py-3 text-center">Direção</th>
                <th className="px-4 py-3 text-left">Data/Hora</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && conversas.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center">
                    <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin mx-auto" />
                  </td>
                </tr>
              ) : conversas.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-gray-400 text-sm">
                    Nenhuma conversa encontrada. Importe um arquivo acima pra começar.
                  </td>
                </tr>
              ) : (
                conversas.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 max-w-xs truncate">{c.contato ?? '—'}</div>
                      {c.tipo && c.tipo !== 'individual' && (
                        <div className="text-[11px] text-gray-400 capitalize">{c.tipo}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{c.telefone ?? '—'}</td>
                    <td className="px-4 py-3">
                      <div className="text-gray-700 max-w-md line-clamp-2">{c.mensagem ?? '—'}</div>
                      {(c.palavras_chave && c.palavras_chave.length > 0) && (
                        <div className="flex items-center gap-1 flex-wrap mt-1.5">
                          {c.palavras_chave.map((p) => (
                            <span
                              key={p}
                              className={`inline-flex items-center gap-0.5 text-[10px] font-semibold rounded-full px-2 py-0.5 border ${PALAVRA_CORES[p] ?? 'bg-gray-100 text-gray-700 border-gray-200'}`}
                            >
                              {p}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 ${
                        c.direcao === 'enviada' ? 'bg-green-50 text-green-700' : 'bg-blue-50 text-blue-700'
                      }`}>
                        <i className={`${c.direcao === 'enviada' ? 'ri-arrow-up-line' : 'ri-arrow-down-line'}`}></i>
                        {c.direcao === 'enviada' ? 'Enviada' : 'Recebida'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDataHora(c.data)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        {totalCount > LIMIT && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <span className="text-xs text-gray-500">
              Mostrando {offset + 1}–{Math.min(offset + LIMIT, totalCount)} de {totalCount}
            </span>
            <div className="flex items-center gap-2">
              <button
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - LIMIT))}
                className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 cursor-pointer"
              >
                <i className="ri-arrow-left-s-line"></i> Anterior
              </button>
              <span className="text-xs text-gray-500">página {Math.floor(offset / LIMIT) + 1} de {totalPages}</span>
              <button
                disabled={offset + LIMIT >= totalCount}
                onClick={() => setOffset(offset + LIMIT)}
                className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 cursor-pointer"
              >
                Próximo <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
        <p className="font-semibold mb-1">Como exportar do WhatsApp Web</p>
        <p className="text-amber-700 leading-relaxed">
          Instale uma extensão como <strong>WhatsApp Scraper</strong> ou <strong>WA Chats Backup</strong> no Chrome.
          Abra o WhatsApp Web, rode a extensão pra exportar as conversas em <strong>CSV</strong> ou <strong>JSON</strong>,
          e suba o arquivo aqui. Os campos de contato, telefone, mensagem, data e direção (enviada/recebida) são reconhecidos automaticamente.
        </p>
      </div>
    </div>
  );
}

function parseCSV(text: string): string[][] {
  const lines = text.trim().split('\n');
  return lines.map((line) => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if ((char === ',' || char === ';' || char === '\t') && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  });
}

function parseDateFlex(val: string): string | null {
  if (!val) return null;
  const d = new Date(val);
  if (!Number.isNaN(d.getTime())) return d.toISOString();
  // tentativa dd/mm/aaaa hh:mm
  const m = val.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2,4})[ T]?(\d{1,2}:\d{2})?/);
  if (m) {
    const dia = m[1].padStart(2, '0');
    const mes = m[2].padStart(2, '0');
    let ano = m[3];
    if (ano.length === 2) ano = `20${ano}`;
    const hora = m[4] ?? '00:00';
    const iso = new Date(`${ano}-${mes}-${dia}T${hora}:00`);
    return Number.isNaN(iso.getTime()) ? null : iso.toISOString();
  }
  return null;
}

function mapRowsToConversas(rows: any[], sourceName: string): any[] {
  if (rows.length === 0) return [];

  let header: string[] = [];
  let dataRows: any[] = [];

  // Se for JSON array de objetos
  if (rows.every((r) => typeof r === 'object' && !Array.isArray(r))) {
    return rows.map((r) => normalizeObject(r, sourceName)).filter((r) => r.contato || r.mensagem || r.telefone);
  }

  // Se for array de arrays (CSV)
  if (Array.isArray(rows[0]) && typeof rows[0][0] === 'string') {
    header = rows[0].map((h: any) => String(h).toLowerCase().trim());
    dataRows = rows.slice(1);
  } else {
    dataRows = rows;
  }

  const getCol = (names: string[]) => {
    for (const n of names) {
      const idx = header.findIndex((h) => h.includes(n));
      if (idx >= 0) return idx;
    }
    return -1;
  };

  const contatoIdx = getCol(['contato', 'contact', 'nome', 'name', 'from', 'remetente', 'sender']);
  const telIdx = getCol(['telefone', 'phone', 'numero', 'number', 'fone', 'celular']);
  const msgIdx = getCol(['mensagem', 'message', 'texto', 'text', 'body', 'conteudo']);
  const dataIdx = getCol(['data', 'date', 'hora', 'time', 'datetime', 'timestamp']);
  const dirIdx = getCol(['direcao', 'direction', 'tipo', 'type', 'enviada', 'recebida', 'sent', 'received']);
  const tipoIdx = getCol(['grupo', 'group', 'chat', 'tipo_chat']);

  return dataRows
    .map((row) => {
      const contato = contatoIdx >= 0 ? String(row[contatoIdx] ?? '').trim() : null;
      const telefone = telIdx >= 0 ? String(row[telIdx] ?? '').trim() : null;
      const mensagem = msgIdx >= 0 ? String(row[msgIdx] ?? '').trim() : null;
      const rawData = dataIdx >= 0 ? String(row[dataIdx] ?? '').trim() : null;
      const rawDir = dirIdx >= 0 ? String(row[dirIdx] ?? '').toLowerCase().trim() : '';
      const rawTipo = tipoIdx >= 0 ? String(row[tipoIdx] ?? '').trim() : '';

      const direcao = rawDir.includes('enviada') || rawDir.includes('sent') || rawDir.includes('outgoing') || rawDir.includes('out')
        ? 'enviada'
        : 'recebida';
      const tipo = rawTipo.includes('grupo') || rawTipo.includes('group') ? 'grupo' : 'individual';

      return {
        contato,
        telefone,
        mensagem,
        direcao,
        tipo,
        data: parseDateFlex(rawData ?? ''),
        arquivo_origem: sourceName,
      };
    })
    .filter((r) => r.contato || r.mensagem || r.telefone);
}

function normalizeObject(r: any, sourceName: string): any {
  const contato = String(r.contato ?? r.contact ?? r.name ?? r.nome ?? r.from ?? r.sender ?? r.chat_name ?? '').trim() || null;
  const telefone = String(r.telefone ?? r.phone ?? r.number ?? r.numero ?? r.phone_number ?? '').trim() || null;
  const mensagem = String(r.mensagem ?? r.message ?? r.text ?? r.body ?? r.content ?? r.texto ?? '').trim() || null;
  const rawDir = String(r.direcao ?? r.direction ?? r.type ?? r.direction_type ?? '').toLowerCase();
  const rawTipo = String(r.tipo ?? r.chat_type ?? r.group ?? '').toLowerCase();
  const direcao = rawDir.includes('enviada') || rawDir.includes('sent') || rawDir.includes('outgoing') || rawDir.includes('out')
    ? 'enviada'
    : 'recebida';
  const tipo = rawTipo.includes('grupo') || rawTipo.includes('group') ? 'grupo' : 'individual';
  return {
    contato,
    telefone,
    mensagem,
    direcao,
    tipo,
    data: parseDateFlex(String(r.data ?? r.date ?? r.timestamp ?? r.time ?? r.datetime ?? '')),
    arquivo_origem: sourceName,
  };
}